<!--@format-->
<template>
    <div class="contact-statuses-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Contact Statuses</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div class="add-buttons-section">
                    <app-button v-if="!isAddSectionVisible" @click="showAddSection">
                        Add New
                    </app-button>
                    <app-button v-else @click="hideAddSection"> Cancel </app-button>
                </div>

                <add-contact-status-section
                    v-if="isAddSectionVisible"
                    :contact-statuses="contactStatuses"
                    :colors="colors"
                    :clientId="clientId"
                />

                <div class="contact-statuses">
                    <contact-status
                        v-for="contactStatus in contactStatuses"
                        :key="contactStatus.id"
                        :contact-status="contactStatus"
                        :colors="colors"
                        :contact-statuses="contactStatuses"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container"></div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const AppColorCircle = () => import('@/components/common/app-color-circle');
const ContactStatus = () =>
    import('@/components/sliding-widgets/contact-statuses-widget/contact-status');
const AddContactStatusSection = () =>
    import('@/components/sliding-widgets/contact-statuses-widget/add-contact-status-section');

import { Sketch } from 'vue-color';
import log, { LogLevel } from '@/scripts/logger';

import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'contact-statuses-widget',

    props: {
        options: {
            type: Object,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'color-picker': Sketch,
        'widget-close-svg': WidgetCloseSvg,
        'app-color-circle': AppColorCircle,
        'add-contact-status-section': AddContactStatusSection,
        'contact-status': ContactStatus,
    },

    data() {
        return {
            clientId: this.widget.options.clientId,

            contactStatuses: [],
            isAddSectionVisible: false,

            colors: this.$helpers.getContactStatusColorOptions(),
        };
    },

    methods: {
        getContactStatuses() {
            this.$api
                .get(`/service/ContactStatuses/?$filter=ClientId eq ${this.clientId}`)
                .then((response) => {
                    this.setContactStatuses(response.data.value);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your contact status.',
                        { error },
                    );
                });
        },

        setContactStatuses(response) {
            this.contactStatuses = response.map((contactStatus) => {
                return {
                    id: contactStatus.Id,
                    name: {
                        value: contactStatus.Name,
                        edit: contactStatus.Name,
                    },
                    color: {
                        value: contactStatus.Color,
                        edit: contactStatus.Color,
                    },
                    isEditShown: false,
                    isUpdating: false,
                    isDeleting: false,
                };
            });
        },

        getContactStatusBackgroundClass(color) {
            return this.$helpers.getContactStatusBackgroundClass(color);
        },

        showAddSection() {
            this.isAddSectionVisible = true;
        },

        hideAddSection() {
            this.isAddSectionVisible = false;
        },

        setContactStatusColor(color) {
            this.contactStatus.color = color.hex;
        },

        updateContactStatus(contactStatus) {
            contactStatus.isUpdating = true;

            var changes = [
                {
                    name: 'Name',
                    value: contactStatus.name.edit,
                },
                {
                    name: 'Color',
                    value: contactStatus.color.edit,
                },
            ];

            this.$api
                .patch(`/api/ContactStatuses/${contactStatus.id}`, changes)
                .then(() => {
                    contactStatus.color.value = contactStatus.color.edit;
                    contactStatus.name.value = contactStatus.name.edit;

                    contactStatus.isUpdating = false;
                    contactStatus.isEditShown = false;
                    EventBus.$emit('contact-status:update');
                })
                .catch((error) => {
                    contactStatus.isUpdating = false;
                    contactStatus.isEditShown = false;
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your contact status.',
                        { error },
                    );
                });
        },

        showContactStatusEdit(contactStatus) {
            contactStatus.isEditShown = true;
        },

        cancelContactStatusEdit(contactStatus) {
            contactStatus.color.edit = contactStatus.color.value;
            contactStatus.name.edit = contactStatus.name.value;
            contactStatus.isEditShown = false;
        },

        updateContactStatusColor(color, contactStatus) {
            contactStatus.color.edit = color;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        deleteContactStatus(contactStatus) {
            contactStatus.isDeleting = true;

            this.$api
                .delete(`/api/ContactStatuses/${contactStatus.id}`)
                .then(() => {
                    contactStatus.isDeleting = false;
                    var matchedContactStatus = this.contactStatuses.find((type) => {
                        return type.id === contactStatus.id;
                    });

                    this.contactStatuses.splice(
                        this.contactStatuses.indexOf(matchedContactStatus),
                        1,
                    );
                    this.$helpers.showSuccessNotification(
                        `The "${contactStatus.name.edit}" contact status has been removed.`,
                    );
                    EventBus.$emit('contact-status:update');
                })
                .catch((error) => {
                    this.isDeleting = false;
                    contactStatus.isEditShown = false;

                    this.$helpers.showFailNotification(
                        `An error occurred while deleting "${contactStatus.name.edit}".`,
                        { level: LogLevel.DEBUG },
                    );
                    log.error(`An error occurred while deleting a contact.`, error);
                });
        },
    },

    created() {
        this.getContactStatuses();
    },
};
</script>

<style>
.contact-statuses-widget .flex-wrapper {
    display: flex;
    align-items: flex-end;
    margin-right: 15px;
}

.contact-statuses-widget .content-wrapper .add-buttons-section .btn {
    margin-bottom: 25px;
}
</style>
